/**
 * 音频文件保存工具
 * 
 * 功能：将 PCM 音频数据转换为 WAV 格式的 Blob 对象
 * 用途：实时录音时保存音频文件，供离线转写使用
 */

/**
 * 将 Float32Array 数据转换为 PCM16 格式的 ArrayBuffer
 */
function convertFloat32ToPCM16(float32Array: Float32Array): ArrayBuffer {
  const buffer = new ArrayBuffer(float32Array.length * 2);
  const view = new DataView(buffer);
  let offset = 0;
  
  for (let i = 0; i < float32Array.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, float32Array[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
  }
  
  return buffer;
}

/**
 * 创建 WAV 文件头
 */
function createWavHeader(
  sampleRate: number,
  channelCount: number,
  dataLength: number
): ArrayBuffer {
  const header = new ArrayBuffer(44);
  const view = new DataView(header);
  
  // 辅助函数：写入 ASCII 字符串
  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };
  
  // "RIFF" chunk descriptor
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true); // File size - 8
  writeString(8, 'WAVE');
  
  // "fmt " sub-chunk
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true); // Subchunk1Size (16 for PCM)
  view.setUint16(20, 1, true); // AudioFormat (1 for PCM)
  view.setUint16(22, channelCount, true); // NumChannels
  view.setUint32(24, sampleRate, true); // SampleRate
  view.setUint32(28, sampleRate * channelCount * 2, true); // ByteRate
  view.setUint16(32, channelCount * 2, true); // BlockAlign
  view.setUint16(34, 16, true); // BitsPerSample
  
  // "data" sub-chunk
  writeString(36, 'data');
  view.setUint32(40, dataLength, true); // Subchunk2Size
  
  return header;
}

/**
 * 将 PCM 数据数组转换为 WAV 格式的 Blob
 * 
 * @param pcmDataList - PCM 音频数据片段列表（Float32Array）
 * @param sampleRate - 采样率（默认 16000 Hz）
 * @param channelCount - 声道数（默认 1，单声道）
 * @returns WAV 格式的 Blob 对象
 */
export function convertPcmToWavBlob(
  pcmDataList: Float32Array[],
  sampleRate: number = 16000,
  channelCount: number = 1
): Blob {
  // 计算总数据长度
  const totalLength = pcmDataList.reduce((sum, arr) => sum + arr.length, 0);
  
  // 合并所有 Float32Array 数据
  const mergedFloat32 = new Float32Array(totalLength);
  let offset = 0;
  for (const chunk of pcmDataList) {
    mergedFloat32.set(chunk, offset);
    offset += chunk.length;
  }
  
  // 转换为 PCM16 格式
  const pcm16Buffer = convertFloat32ToPCM16(mergedFloat32);
  
  // 创建 WAV 文件头
  const wavHeader = createWavHeader(sampleRate, channelCount, pcm16Buffer.byteLength);
  
  // 合并文件头和音频数据
  const wavBlob = new Blob([wavHeader, pcm16Buffer], { type: 'audio/wav' });
  
  return wavBlob;
}

/**
 * 创建音频文件并生成 File 对象
 * 
 * @param pcmDataList - PCM 音频数据片段列表
 * @param fileName - 文件名（默认带时间戳）
 * @param sampleRate - 采样率
 * @param channelCount - 声道数
 * @returns File 对象
 */
export function createAudioFile(
  pcmDataList: Float32Array[],
  fileName?: string,
  sampleRate: number = 16000,
  channelCount: number = 1
): File {
  const blob = convertPcmToWavBlob(pcmDataList, sampleRate, channelCount);
  const defaultFileName = fileName || `recording_${Date.now()}.wav`;
  
  return new File([blob], defaultFileName, { type: 'audio/wav' });
}

/**
 * 下载音频文件到本地（调试用）
 */
export function downloadAudioFile(
  pcmDataList: Float32Array[],
  fileName?: string,
  sampleRate: number = 16000,
  channelCount: number = 1
): void {
  const blob = convertPcmToWavBlob(pcmDataList, sampleRate, channelCount);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || `recording_${Date.now()}.wav`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
